<template>
  <div class="seeker-job-card">
    <section class="relative">
      <a class="block w-full" :href="resolvedHref" @click="emitJobClick">
        <SeekerJobCardDescription ref="jobDescription" :job="job" :accountID="account.id" :showBanner="showBanner" />
      </a>
      <Button
        v-if="displayDots"
        id="modal-toggle"
        class="seeker-job-card-modal absolute top-8 right-8"
        :class="popupClasses"
        variant="text"
        :aria-label="modalToggleLabel"
        @click="$emit('manage-job', job)"
      >
        <MoreIcon />
      </Button>
    </section>
    <slot></slot>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { RawLocation } from 'vue-router';
import Button from '@/components/button/Button.vue';
import MoreIcon from '@/graphics/icons/MoreIcon.vue';
import helperStatus from '@/helpers/status';
import { Account } from '@/models/account';

import SeekerJobCardDescription from './SeekerJobCardDescription.vue';

export default defineComponent({
  name: 'SeekerJobCard',
  components: {
    Button,
    MoreIcon,
    SeekerJobCardDescription,
  },
  props: {
    job: {
      type: Object as () => any,
      required: true,
    },
    account: {
      type: Object as () => Account,
      required: true,
    },
    /** When true, click is handled by parent and router navigation is prevented */
    preventNavigationOnClick: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click', 'manage-job'],
  data(): {
    helperStatus: typeof helperStatus;
  } {
    return {
      helperStatus,
    };
  },
  computed: {
    // UI State
    modalToggleLabel(): string {
      return `Manage your job: ${this.job.title}`;
    },
    showBanner(): boolean {
      // always show banner for pending jobs
      if (this.job.status === 'PENDING') return true;

      // if job is not active, don't show banner
      if (!this.job.isActive) return false;

      // finally, show the banner if job has new applicants
      return (this.job.receivedApplications?.totalCount ?? 0) > 0;
    },
    popupClasses(): Record<string, boolean> {
      return { '__with-banner': this.showBanner };
    },
    displayDots(): boolean {
      const parentOwnsJob = this.account.id === this.job.parent.id;
      const hasOpenApplicants = (this.job.applications?.totalCount ?? 0) > 0;

      // some jobs are owned by the parent, some are owned by family concierge. If the parent owns the job, we allow them to manage the job.
      // if the job has open applicants, we show the menu because it has a link to the applicants page
      return parentOwnsJob || hasOpenApplicants;
    },
    totalNewApplicants(): number {
      return this.job.receivedApplications?.totalCount ?? 0;
    },
    jobLink(): RawLocation {
      if (this.totalNewApplicants > 0) {
        return { name: 'applications', params: { job_uuid: this.job.id } };
      }
      const isPending = this.job.status === 'PENDING';
      if (this.job.isActive || isPending) {
        return { name: 'seeker-jobs-open' };
      }
      return { name: 'seeker-jobs-closed' };
    },
    resolvedHref(): string {
      return this.$router.resolve(this.jobLink).href;
    },
  },
  methods: {
    emitJobClick(e: Event) {
      this.$emit('click', this, this.job, e);
    },
  },
});
</script>

<style lang="scss" scoped>
.seeker-job-card {
  flex-flow: column;
  background: white;
  box-shadow: 0 2px 4px 0 rgba(5, 7, 15, 0.2);
  border-radius: 4px;
  display: flex;
  transition: 0.5s box-shadow ease-in;

  &:hover {
    box-shadow: 0 4px 16px 0 rgba(5, 7, 15, 0.2);
  }
}

.seeker-job-card-modal {
  margin-left: auto;
  position: absolute;

  &.__with-banner {
    top: 2.5rem;
  }
}
</style>
